import React from "react";
import { Card, Row, Col, Statistic, Divider, Typography, Tag, Empty, Table } from "antd";
import { FilePreview } from "../../interfaces/BulkPayment";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

interface DetailsSummaryProps {
  transactions: FilePreview[];
}

const getColor = (channel: string) => {
  switch (channel.toLowerCase()) {
    case "mpesa":
      return "#00C851";
    case "airtel":
      return "#E53E3E";
    case "orange":
      return "#FF6B35";
    default:
      return "#4A90E2";
  }
};

const getChannelGradient = (channel: string) => {
  switch (channel.toLowerCase()) {
    case "mpesa":
      return "linear-gradient(135deg, #00C851 0%, #00A844 100%)";
    case "airtel":
      return "linear-gradient(135deg, #E53E3E 0%, #C53030 100%)";
    case "orange":
      return "linear-gradient(135deg, #FF6B35 0%, #E55B2B 100%)";
    default:
      return "linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)";
  }
};

const DetailsSummary: React.FC<DetailsSummaryProps> = ({ transactions }) => {
  const { t } = useTranslation();
  
  // Calculate total amount and transactions by channel
  const totalAmount = transactions.reduce((sum, tx) => sum + tx.amount, 0);
  // Get the currency from the first transaction (all should be the same per file)
  const currency = transactions[0]?.currency || '';
  const transactionsByChannel: { [channel: string]: { count: number; amount: number } } = {};

  transactions.forEach((tx) => {
    const channel: string = tx.channel && typeof tx.channel === 'string' && tx.channel.trim() !== '' ? tx.channel : t('general.unknown') || 'Unknown';
    if (!transactionsByChannel[channel]) {
      transactionsByChannel[channel] = { count: 0, amount: 0 };
    }
    transactionsByChannel[channel].count += 1;
    transactionsByChannel[channel].amount += tx.amount;
  });

  const StatCard = ({ title, value, color, isAmount }: { title: string; value: number; color: string; icon?: string; isAmount?: boolean }) => (
    <div style={{
      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
      border: '1px solid #e2e8f0',
      borderRadius: '16px',
      padding: '24px',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = '0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)';
    }}>
      <div style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: '60px',
        height: '60px',
        background: `${color}15`,
        borderRadius: '0 16px 0 60px',
      }} />
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '12px'
      }}>
        <Text style={{ 
          color: '#64748b', 
          fontSize: '14px', 
          fontWeight: 500,
          letterSpacing: '0.025em'
        }}>
          {title}
        </Text>
        <div style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: color,
        }} />
      </div>
      <div style={{
        fontSize: '28px',
        fontWeight: 700,
        color: '#1e293b',
        lineHeight: 1.2,
      }}>
        {isAmount 
          ? `${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
          : value.toLocaleString()
        }
      </div>
    </div>
  );

  return (
    <div style={{
      background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
      borderRadius: '20px',
      padding: '32px',
      marginBottom: '24px',
    }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: 700,
          color: '#1e293b',
          margin: 0,
          marginBottom: '8px',
          letterSpacing: '-0.025em'
        }}>
          {t('general.details.summaryTitle')}
        </h2>
        <Text style={{
          color: '#64748b',
          fontSize: '14px',
          fontWeight: 500
        }}>
          {t('general.details.overviewText', 'Transaction overview and payment channel breakdown')}
        </Text>
      </div>

      {/* Overview Stats */}
      <Row gutter={[24, 24]} style={{ marginBottom: '40px' }}>
        <Col xs={24} sm={8}>
          <StatCard
            title={t('general.details.totalTransactions')}
            value={transactions.length}
            color="#3b82f6"
          />
        </Col>
        <Col xs={24} sm={8}>
          <StatCard
            title={t('general.details.totalAmount', { currency })}
            value={totalAmount}
            color="#059669"
            isAmount={true}
          />
        </Col>
        <Col xs={24} sm={8}>
          <StatCard
            title={t('general.details.channels')}
            value={Object.keys(transactionsByChannel).length}
            color="#dc2626"
          />
        </Col>
      </Row>

      {/* Channel Breakdown Table */}
      <div>
        <h3 style={{
          fontSize: '18px',
          fontWeight: 600,
          color: '#1e293b',
          margin: '0 0 20px 0',
          letterSpacing: '-0.025em'
        }}>
          {t('general.details.transactionsByChannel')}
        </h3>
        {Object.entries(transactionsByChannel).length === 0 ? (
          <div style={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '40px',
            textAlign: 'center'
          }}>
            <Empty description={t('general.details.noChannelData')} />
          </div>
        ) : (
          <div style={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
          }}>
            <Table
              dataSource={Object.entries(transactionsByChannel).map(([channel, data], index) => ({
                key: index,
                channel,
                transactions: data.count,
                amount: data.amount
              }))}
              pagination={false}
              size="large"
              columns={[
                {
                  title: t('general.channel'),
                  dataIndex: 'channel',
                  key: 'channel',
                  render: (channel: string) => (
                    <div style={{
                      fontSize: '17px',
                      fontWeight: 600,
                      color: '#000000',
                    }}>
                      {channel}
                    </div>
                  ),
                },
                {
                  title: t('general.records', t('general.entries')),
                  dataIndex: 'transactions',
                  key: 'transactions',
                  align: 'center' as const,
                  render: (count: number) => (
                    <div style={{
                      fontSize: '17px',
                      fontWeight: 600,
                      color: '#000000',
                    }}>
                      {count.toLocaleString()}
                    </div>
                  ),
                },
                {
                  title: `${t('general.amount')}${currency ? ` (${currency})` : ''}`,
                  dataIndex: 'amount',
                  key: 'amount',
                  align: 'right' as const,
                  render: (amount: number) => (
                    <div style={{
                      fontSize: '17px',
                      fontWeight: 700,
                      color: '#000000',
                    }}>
                      {currency}{amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  ),
                },
              ]}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DetailsSummary;